import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import SortableList, { DragHandle } from "../../components/SortableList";

const BLANK = {
  slug: "", name: "", name_en: "", name_de: "", category_id: "",
  short_description: "", description: "",
  features: [], specs: {}, images: [], badges: [],
  min_order: "1 Adet", suitable_room_types: [],
  star_requirement: { min_star: 0, required: false },
  featured: false, order: 0, active: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/categories", { params: { active_only: false } }).then((r) => setCats(r.data));
  };
  useEffect(load, []);

  const openNew = () => { setForm(BLANK); setEditingId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      ...BLANK, ...p,
      features: p.features || [], images: p.images || [], badges: p.badges || [], specs: p.specs || {},
      star_requirement: p.star_requirement || { min_star: 0, required: false },
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      features: Array.isArray(form.features) ? form.features : String(form.features).split("\n").filter(Boolean),
      images: Array.isArray(form.images) ? form.images : String(form.images).split("\n").filter(Boolean),
      badges: Array.isArray(form.badges) ? form.badges : String(form.badges).split(",").map(s => s.trim()).filter(Boolean),
      order: Number(form.order || 0),
      star_requirement: {
        min_star: Number(form.star_requirement?.min_star || 0),
        required: !!form.star_requirement?.required,
      },
    };
    try {
      if (editingId) await api.put(`/admin/products/${editingId}`, body);
      else await api.post("/admin/products", body);
      toast.success("Kaydedildi");
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Silindi");
    load();
  };

  const persistReorder = async (next) => {
    setProducts(next);
    try {
      await api.put(`/admin/products/_reorder`, { ids: next.map((p) => p.id) });
      toast.success("Sıralama güncellendi");
    } catch { toast.error("Sıra kaydedilemedi"); load(); }
  };

  const catName = (id) => cats.find((c) => c.id === id)?.name || "-";

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-serif text-3xl font-semibold text-navy">Ürünler</h1>
          <p className="text-sm text-gray-500 mt-1">{products.length} ürün — sıralamak için sürükleyin</p>
        </div>
        <Button onClick={openNew} className="bg-gold hover:bg-[#c49d58]" data-testid="admin-product-new">
          <Plus size={16} className="mr-1" /> Yeni Ürün
        </Button>
      </div>

      <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
        <div className="bg-softbg text-xs uppercase tracking-wider text-gray-500 px-3 py-3 grid grid-cols-[auto_2fr_1.2fr_1fr_70px_70px_120px] gap-3 items-center">
          <span></span><span>Ürün</span><span>Kategori</span><span>Badge</span><span className="text-center">Öne</span><span className="text-center">Aktif</span><span className="text-right">İşlem</span>
        </div>
        <div className="p-2">
          <SortableList items={products} setItems={persistReorder} keyOf={(p) => p.id}>
            {(p, dragProps) => (
              <div className="grid grid-cols-[auto_2fr_1.2fr_1fr_70px_70px_120px] gap-3 items-center bg-white border border-gray-100 rounded-md px-3 py-2 mb-2 hover:bg-softbg/60" data-testid={`admin-product-row-${p.slug}`}>
                <DragHandle {...dragProps} />
                <div className="flex items-center gap-3 min-w-0">
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="h-10 w-10 rounded-md object-cover flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="font-medium text-navy truncate">{p.name}</div>
                    <div className="text-[11px] text-gray-400 truncate">/{p.slug}</div>
                  </div>
                </div>
                <span className="text-gray-600 text-sm truncate">{catName(p.category_id)}</span>
                <span className="text-xs text-gray-500 truncate">{(p.badges || []).join(", ")}</span>
                <span className="text-center">{p.featured ? <Star size={14} className="text-gold inline" /> : "-"}</span>
                <span className="text-center">{p.active ? <span className="text-emerald-600">●</span> : <span className="text-gray-300">●</span>}</span>
                <div className="text-right">
                  <button onClick={() => openEdit(p)} className="text-gray-500 hover:text-teal p-1.5" data-testid={`admin-product-edit-${p.slug}`}><Pencil size={14} /></button>
                  <button onClick={() => del(p.id)} className="text-gray-500 hover:text-rose-600 p-1.5" data-testid={`admin-product-delete-${p.slug}`}><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </SortableList>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto admin-scroll">
          <DialogHeader><DialogTitle className="heading-serif">{editingId ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <F label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} required />
              <F label="Ad (TR)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Kategori</Label>
                <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">Seç</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <F label="Min. Sipariş" value={form.min_order} onChange={(v) => setForm({ ...form, min_order: v })} />
            </div>
            <F label="Kısa Açıklama" value={form.short_description} onChange={(v) => setForm({ ...form, short_description: v })} />
            <div>
              <Label className="text-xs">Açıklama (uzun)</Label>
              <Textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Özellikler (her satır bir madde)</Label>
              <Textarea rows={3} value={(form.features || []).join("\n")} onChange={(e) => setForm({ ...form, features: e.target.value.split("\n") })} />
            </div>
            <div>
              <Label className="text-xs">Görseller (her satır bir URL)</Label>
              <Textarea rows={2} value={(form.images || []).join("\n")} onChange={(e) => setForm({ ...form, images: e.target.value.split("\n") })} />
            </div>
            <F label="Badge (virgülle — premium, logo_baskili, toptan, stokta)" value={(form.badges || []).join(", ")} onChange={(v) => setForm({ ...form, badges: v.split(",").map(s => s.trim()).filter(Boolean) })} />
            <div className="grid grid-cols-3 gap-3">
              <F label="Sıra" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
              <F label="Min. Yıldız" type="number" value={form.star_requirement?.min_star || 0} onChange={(v) => setForm({ ...form, star_requirement: { ...form.star_requirement, min_star: Number(v) } })} />
              <div className="flex items-center gap-2 pt-5">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                <Label className="text-xs">Öne Çıkan</Label>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.star_requirement?.required} onChange={(e) => setForm({ ...form, star_requirement: { ...form.star_requirement, required: e.target.checked } })} /> Yıldız için zorunlu</label>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button type="submit" className="bg-gold hover:bg-[#c49d58]">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, value, onChange, type = "text", required }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} required={required} />
  </div>
);
